import Utf8 from './Utf8'
import { PlaceInGlobalNameSpace } from '../utils/Utils'

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */
/*  Base64 class: Base 64 encoding / decoding (c) Chris Veness 2002-2012                          */
/*    note: depends on Utf8 class                                                                 */
/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -  */

class Base64 {
    /* eslint-disable no-param-reassign */
    /* eslint-disable no-bitwise */
    /* eslint-disable no-mixed-operators */
    private static readonly code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='

    /**
     * Encode string into Base64, as defined by RFC 4648 [http://tools.ietf.org/html/rfc4648]
     * (instance method extending String object). As per RFC 4648, no newlines are added.
     *
     * @param {String} str The string to be encoded as base-64
     * @param {Boolean} [utf8encode=false] Flag to indicate whether str is Unicode string to be encoded
     *   to UTF8 before conversion to base64; otherwise string is assumed to be 8-bit characters
     * @returns {String} Base64-encoded string
     */
    public static encode(str: string, utf8encode?: boolean): string {
        // http://tools.ietf.org/html/rfc4648
        utf8encode = typeof utf8encode === 'undefined' ? false : utf8encode
        let o1
        let o2
        let o3
        let bits
        let h1
        let h2
        let h3
        let h4
        const e = []
        let pad = ''
        let c
        let plain
        let coded
        const b64 = Base64.code

        plain = utf8encode ? Utf8.encode(str) : str

        c = plain.length % 3 // pad string to length of multiple of 3
        if (c > 0) {
            // eslint-disable-next-line
            while (c++ < 3) {
                pad += '='
                plain += '\0'
            }
        }
        // note: doing padding here saves us doing special-case packing for trailing 1 or 2 chars

        for (c = 0; c < plain.length; c += 3) {
            // pack three octets into four hexets
            o1 = plain.charCodeAt(c)
            o2 = plain.charCodeAt(c + 1)
            o3 = plain.charCodeAt(c + 2)

            bits = (o1 << 16) | (o2 << 8) | o3

            h1 = (bits >> 18) & 0x3f
            h2 = (bits >> 12) & 0x3f
            h3 = (bits >> 6) & 0x3f
            h4 = bits & 0x3f

            // use hextets to index into code string
            e[c / 3] = b64.charAt(h1) + b64.charAt(h2) + b64.charAt(h3) + b64.charAt(h4)
        }
        coded = e.join('') // join() is far faster than repeated string concatenation in IE

        // replace 'A's from padded nulls with '='s
        coded = coded.slice(0, coded.length - pad.length) + pad

        return coded
    }

    /**
     * Encode string into base64url, as defined by RFC 4648 [https://datatracker.ietf.org/doc/html/rfc4648#section-5]
     *
     * @param {String} str The string to be encoded as base64url
     * @returns {String} base64url-encoded string
     */
    public static urlEncode(str: string): string {
        return Base64.encode(str, true).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
    }

    /**
     * Decode string from Base64, as defined by RFC 4648 [http://tools.ietf.org/html/rfc4648]
     * (instance method extending String object). As per RFC 4648, newlines are not catered for.
     *
     * @param {String} str The string to be decoded from base-64
     * @param {Boolean} [utf8decode=false] Flag to indicate whether str is Unicode string to be decoded
     *   from UTF8 after conversion from base64
     * @returns {String} decoded string
     */
    public static decode(str: string, utf8decode?: boolean): string {
        utf8decode = typeof utf8decode === 'undefined' ? false : utf8decode
        let o1
        let o2
        let o3
        let h1
        let h2
        let h3
        let h4
        let bits
        const d = []
        const b64 = Base64.code

        const coded = utf8decode ? Utf8.decode(str) : str

        for (let c = 0; c < coded.length; c += 4) {
            // unpack four hexets into three octets
            h1 = b64.indexOf(coded.charAt(c))
            h2 = b64.indexOf(coded.charAt(c + 1))
            h3 = b64.indexOf(coded.charAt(c + 2))
            h4 = b64.indexOf(coded.charAt(c + 3))

            bits = (h1 << 18) | (h2 << 12) | (h3 << 6) | h4

            o1 = (bits >>> 16) & 0xff
            o2 = (bits >>> 8) & 0xff
            o3 = bits & 0xff

            d[c / 4] = String.fromCharCode(o1, o2, o3)
            // check for padding
            if (h4 === 0x40) d[c / 4] = String.fromCharCode(o1, o2)
            if (h3 === 0x40) d[c / 4] = String.fromCharCode(o1)
        }
        const plain = d.join('') // join() is far faster than repeated string concatenation in IE

        return utf8decode ? Utf8.decode(plain) : plain
    }
}

export default Base64
PlaceInGlobalNameSpace('Base64', Base64)
